import { chatIdMutation } from "@/shared/api/queries"

import { groupSettingsApi } from "./get-settings"

import { toExportGroupSettingsError } from "../lib"

import type {
  BulkImportSettingsRequest,
  BulkImportSettingsResponse,
  ExportedSettingsFile,
  ExportGroupSettingsDirectRequest,
  ExportGroupSettingsDirectResponse,
  ExportGroupSettingsRequest,
  ExportGroupSettingsResponse,
  ImportGroupSettingsRequest,
  ImportGroupSettingsResponse,
  UnlockCustomGreetingRequest,
  UnlockCustomGreetingResponse
} from "./types"

const importsCommands = (settingsJson: ExportedSettingsFile): boolean =>
  Object.prototype.hasOwnProperty.call(settingsJson.settings, "commands")

export const createGroupSettingsApi = groupSettingsApi
  .enhanceEndpoints({
    addTagTypes: ["Balance", "DistributionChats"]
  })
  .injectEndpoints({
    endpoints: builder => ({
      unlockCustomGreeting: builder.mutation<UnlockCustomGreetingResponse, UnlockCustomGreetingRequest>({
        queryFn: chatIdMutation<UnlockCustomGreetingRequest, UnlockCustomGreetingResponse>(chatId => ({
          url: `/api/v1/group/rating/notifications/unlock-greeting?chat_id=${chatId}`,
          method: "POST"
        })),
        invalidatesTags: (_result, error, { chatId }) =>
          error
            ? []
            : [{ type: "NotificationPreferences" as const, id: `CHAT_${chatId}` }, "Balance", "DistributionChats"]
      }),

      exportGroupSettings: builder.mutation<ExportGroupSettingsResponse, ExportGroupSettingsRequest>({
        queryFn: chatIdMutation<ExportGroupSettingsRequest, ExportGroupSettingsResponse>(
          (chatId, arg) => ({
            url: `/api/v1/group/${chatId}/settings/export`,
            method: "POST",
            body: { sections: arg.sections }
          }),
          {
            transformErrorResponse: toExportGroupSettingsError
          }
        )
      }),

      exportGroupSettingsDirect: builder.mutation<ExportGroupSettingsDirectResponse, ExportGroupSettingsDirectRequest>({
        queryFn: chatIdMutation<ExportGroupSettingsDirectRequest, ExportGroupSettingsDirectResponse>((chatId, arg) => ({
          url: `/api/v1/group/${chatId}/settings/export-direct`,
          method: "POST",
          body: { sections: arg.sections }
        }))
      }),

      importGroupSettings: builder.mutation<ImportGroupSettingsResponse, ImportGroupSettingsRequest>({
        queryFn: chatIdMutation<ImportGroupSettingsRequest, ImportGroupSettingsResponse>((chatId, arg) => ({
          url: `/api/v1/group/${chatId}/settings/import`,
          method: "POST",
          body: { settings_json: arg.settingsJson }
        })),
        invalidatesTags: (_result, error, { chatId }) =>
          error
            ? []
            : [
                { type: "GroupSettings" as const, id: `CHAT_${chatId}` },
                { type: "Commands" as const, id: `CHAT_${chatId}` }
              ]
      }),

      bulkImportSettings: builder.mutation<BulkImportSettingsResponse, BulkImportSettingsRequest>({
        query: arg => ({
          url: `/api/v1/group/settings/bulk-import`,
          method: "POST",
          body: {
            target_chat_ids: arg.targetChatIds,
            settings_json: arg.settingsJson
          }
        }),
        invalidatesTags: (_result, error, { targetChatIds, settingsJson }) => {
          if (error) {
            return []
          }

          const groupSettingsTags = targetChatIds.map(id => ({ type: "GroupSettings" as const, id: `CHAT_${id}` }))

          if (!importsCommands(settingsJson)) {
            return groupSettingsTags
          }

          return [...groupSettingsTags, ...targetChatIds.map(id => ({ type: "Commands" as const, id: `CHAT_${id}` }))]
        }
      })
    })
  })

export const {
  useUnlockCustomGreetingMutation,
  useExportGroupSettingsMutation,
  useExportGroupSettingsDirectMutation,
  useImportGroupSettingsMutation,
  useBulkImportSettingsMutation
} = createGroupSettingsApi
